import { createAsyncThunk } from '@reduxjs/toolkit';
import { addLoading, removeLoading } from '../appAction';
import { Enums, Value } from '@utils/constants';
import paths from '@utils/constants/paths';
import { axios } from '@utils/lib';
import { IListRes } from '@interfaces/apis/response-data';
import { format } from 'date-fns';

export const getReceptionListAsync = createAsyncThunk(
  'driver-history',
  async (query: IReceptionManagementQuery, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const searchQuery: Record<string, string> = {
      page: String(query.page ?? Value.TABLE_PAGE_INIT),
    };
    if (query.accessTime) searchQuery.accessTime = query.accessTime;
    if (query.exitTime) searchQuery.exitTime = query.exitTime;
    if (query.companyName) searchQuery.companyName = query.companyName;
    if (query.securityBoxName)
      searchQuery.securityBoxName = query.securityBoxName;
    if (query.fullNameUser) searchQuery.fullName = query.fullNameUser;
    if (query.phoneNumber) searchQuery.phoneNumber = query.phoneNumber;
    if (query.stayingStart) searchQuery.stayingStart = query.stayingStart;
    if (query.stayingEnd) searchQuery.stayingEnd = query.stayingEnd;
    if (query.pageSize) searchQuery.pageSize = query.pageSize;
    searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = new URLSearchParams(searchQuery).toString();
    try {
      const { data } = await axios.request<IListRes<IReceptionManagement>>(
        Enums.HTTP_METHOD.GET,
        paths.RECEPTION_MANAGEMENT + '?' + params
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const getOneDayReportAsync = createAsyncThunk(
  'driver-history/daily-report',
  async (date: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const searchQuery: Record<string, string> = {};
    if (date) {
      searchQuery.accessTime = date;
      searchQuery.exitTime = date;
    }
    searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = new URLSearchParams(searchQuery).toString();
    try {
      const { data } = await axios.request<IOneDayVisitStat[]>(
        Enums.HTTP_METHOD.GET,
        paths.RECEPTION_DAILY_REPORT + '?' + params
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const exportDailyReportAsync = createAsyncThunk(
  'driver-history/export-daily-report',
  async (query: IReceptionManagementQuery, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const searchQuery: Record<string, string> = {};
      if (!query.accessTime) return;
      searchQuery.accessTime = query.accessTime;
      searchQuery.exitTime = query.accessTime;
      searchQuery.tenantName = query.tenantName ?? '';
      searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams(searchQuery).toString();

      const response = await axios.request<Blob>(
        Enums.HTTP_METHOD.GET,
        paths.DAILY_ACCESS_REPORT_EXPORT + '?' + params,
        undefined,
        {
          responseType: 'blob',
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      );

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateTime = format(new Date(query.accessTime), 'yyyyMMdd');
      link.href = downloadUrl;
      link.setAttribute('download', `入館履歴報告_${dateTime}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const exportReceptionCSVAsync = createAsyncThunk(
  'driver-history/export',
  async (query: IReceptionManagementQuery, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const searchQuery: Record<string, string> = {
        page: String(query.page ?? Value.TABLE_PAGE_INIT),
      };
      if (query.accessTime) searchQuery.accessTime = query.accessTime;
      if (query.exitTime) searchQuery.exitTime = query.exitTime;
      if (query.companyName) searchQuery.companyName = query.companyName;
      if (query.deviceId) searchQuery.deviceId = query.deviceId;
      if (query.fullNameUser) searchQuery.fullName = query.fullNameUser;
      if (query.phoneNumber) searchQuery.phoneNumber = query.phoneNumber;
      if (query.stayingStart) searchQuery.stayingStart = query.stayingStart;
      if (query.stayingEnd) searchQuery.stayingEnd = query.stayingEnd;
      searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams(searchQuery).toString();

      const response = await axios.request<Blob>(
        Enums.HTTP_METHOD.GET,
        paths.RECEPTION_EXPORT + '?' + params,
        undefined,
        {
          responseType: 'blob',
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      );

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const dateTime = format(new Date(), 'yyyyMMdd');
      link.setAttribute('download', `受付履歴_${dateTime}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const forceCheckoutDriverAsync = createAsyncThunk(
  'api/forceCheckoutDriverAsync',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request<IReceptionManagement>(
        Enums.HTTP_METHOD.PUT,
        paths.FORCE_CHECKOUT.replace(':id', id)
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);
